import type {
  FulfillmentEvent,
  FulfillmentOrderLine,
  SourcingComparisonOutcome,
  SourcingPlatform,
  SourcingSellerType,
} from '@/infrastructure/FulfillmentAdminClient';
import { payloadOf } from '@/pages/fulfillment/viewOrders/activityLog';
import { OUTCOME_LABELS, SOURCING_PLATFORM_LABELS, sellerTypeLabel } from '@/utils/fulfillmentSourcingDetail';
import { instantMs, kstTimestamp } from '@/utils/fulfillmentTime';
import { deliveryCompanyLabel } from './DeliveryCompanySelect';

/**
 * The line edits that carry a purchase value (SSD-7664): the agent-filled purchase fields, cost among them, the seller
 * type with its price comparison, and the purchase a gig worker records (SSD-7779). Each names its actor, or its source.
 */
export const EDIT_LOG_OPERATIONS = new Set([
  'LINE_AGENT_TRACKING_UPDATED',
  'LINE_SOURCING_DETAIL_UPDATED',
  'LINE_PURCHASE_RECORDED',
]);

/** A gig worker's purchase holds its values under `purchase` and what it replaced under `previous` (SSD-7779). */
const PURCHASE_FIELDS: Record<string, string> = {
  orderNumber: 'purchaseOrderNumber',
  orderDate: 'purchaseOrderDate',
  platform: 'purchasePlatform',
};

/** The words the line table and its editors already use, so a row reads like the field it changed. */
const FIELD_LABELS: Record<string, string> = {
  purchaseOrderNumber: 'Order No.',
  purchaseOrderDate: 'Order Date',
  purchasePlatform: 'Purchase platform',
  productCost: 'Product Cost',
  purchaseUrl: 'CP Link',
  lineTrackingNumber: 'Tracking Number',
  lineDeliveryCompany: 'Delivery company',
  size: 'Size',
  sellerType: 'Seller type',
  sourcingComparison: 'Price comparison',
};

/** Rows of one save keep the order of the line table's columns rather than the order the request listed them. */
const FIELD_ORDER = Object.keys(FIELD_LABELS);

/** The events hold no value from before the save; saying so is truer than guessing one from an earlier event. */
export const PREVIOUS_NOT_RECORDED = 'previous not recorded';
export const CLEARED = 'cleared';
/** A previous value the server kept as null: nothing was set before, which is not the same as someone clearing it. */
export const NONE = 'none';
/** A save of spaces only: shown as such, since what the server kept from it is not in the event. */
export const BLANK = '(blank)';

export interface EditLogRow {
  id: string;
  at: string;
  actor: string;
  line: string;
  field: string;
  value: string;
  previous: string;
}

const record = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

/** One option as the comparison table shows it: platform, landed total, and a mark on the one bought (SSD-7779). */
const alternativeText = (alternative: Record<string, unknown>, chosen: unknown): string => {
  const platform = typeof alternative['platform'] === 'string' ? alternative['platform'] : '';
  const name = SOURCING_PLATFORM_LABELS[platform as SourcingPlatform] ?? (platform || 'Option');
  const seller =
    typeof alternative['sellerName'] === 'string' && alternative['sellerName'] ? ` ${alternative['sellerName']}` : '';
  // Only a landed total is comparable across options; items alone are named as such (SSD-7779).
  const landed = alternative['landedTotalKrw'];
  const items = alternative['itemsTotalKrw'];
  const price =
    typeof landed === 'number'
      ? ` ₩${landed.toLocaleString('en-US')}`
      : typeof items === 'number'
        ? ` items ₩${items.toLocaleString('en-US')}`
        : '';
  const pieces =
    typeof alternative['quantityBasis'] === 'number' && alternative['quantityBasis'] !== 1
      ? ` ×${alternative['quantityBasis']}`
      : '';
  const stock = typeof alternative['stockStatus'] === 'string' ? ` ${alternative['stockStatus']}` : '';
  const bought = chosen !== undefined && alternative['alternativeId'] === chosen ? ' (bought)' : '';
  return `${name}${seller}${pieces}${price}${stock}${bought}`;
};

// Two saves that change only an option's price or the bought option must still read differently (SSD-7779).
const comparisonText = (value: Record<string, unknown>): string => {
  const outcome = typeof value['outcome'] === 'string' ? value['outcome'] : '';
  const label = OUTCOME_LABELS[outcome as SourcingComparisonOutcome] ?? (outcome || 'Comparison');
  const alternatives = Array.isArray(value['alternatives'])
    ? value['alternatives'].map(record).filter((a): a is Record<string, unknown> => a !== undefined)
    : [];
  if (alternatives.length === 0) {
    return label;
  }
  return `${label}: ${alternatives.map((a) => alternativeText(a, value['chosenAlternativeId'])).join(' · ')}`;
};

/** What the save set the field to, in the words the line table shows it with. */
export const editValueText = (field: string, value: unknown): string => {
  if (field === 'lineDeliveryCompany' && (value === null || typeof value === 'string')) {
    return deliveryCompanyLabel(value);
  }
  if (value === null) {
    return CLEARED;
  }
  if (value === undefined) {
    return '—';
  }
  if (typeof value === 'string' && value.trim() === '') {
    return BLANK;
  }
  if (field === 'productCost' && typeof value === 'number') {
    return `₩${value.toLocaleString('en-US')}`;
  }
  if (field === 'purchasePlatform' && typeof value === 'string') {
    return SOURCING_PLATFORM_LABELS[value as SourcingPlatform] ?? value;
  }
  if (field === 'sellerType' && typeof value === 'string') {
    return sellerTypeLabel(value as SourcingSellerType);
  }
  const nested = record(value);
  if (field === 'sourcingComparison' && nested) {
    return comparisonText(nested);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const lineText = (lineNo: unknown, skuByLine: Map<number, string>): string => {
  if (typeof lineNo !== 'number') {
    return '—';
  }
  const sku = skuByLine.get(lineNo);
  return sku ? `Line ${lineNo} · ${sku}` : `Line ${lineNo}`;
};

/** The values a save set and, where the event keeps them, the values it replaced, keyed by the editors' field names. */
const valuesOf = (
  operation: string,
  payload: Record<string, unknown>,
): { changes: Record<string, unknown>; previous: Record<string, unknown> } => {
  if (operation === 'LINE_PURCHASE_RECORDED') {
    const purchase = record(payload['purchase']) ?? {};
    const before = record(payload['previous']);
    const changes: Record<string, unknown> = {};
    const previous: Record<string, unknown> = {};
    for (const [key, field] of Object.entries(PURCHASE_FIELDS)) {
      // The handler writes all three every time; a field it wrote back unchanged is not an edit.
      if (!(key in purchase) || (before && key in before && before[key] === purchase[key])) {
        continue;
      }
      changes[field] = purchase[key];
      if (before && key in before) {
        previous[field] = before[key];
      }
    }
    return { changes, previous };
  }
  const changes = record(payload['changes']) ?? {};
  const previous: Record<string, unknown> = {};
  const sizeChange = record(payload['sizeChange']);
  if (sizeChange && 'previous' in sizeChange) {
    // A size that was never set is null, which reads as cleared rather than as a save of spaces (SSD-7779).
    previous['size'] = sizeChange['previous'];
  }
  // A replaced parcel is kept as previousTracking (Facade master) or replacedImportedTracking (Facade dev), a removed
  // one as removedTracking; any of them names what was there.
  const replaced =
    record(payload['previousTracking']) ??
    record(payload['replacedImportedTracking']) ??
    record(payload['removedTracking']);
  if (replaced && typeof replaced['number'] === 'string') {
    previous['lineTrackingNumber'] = replaced['number'];
    if ('carrier' in replaced) {
      previous['lineDeliveryCompany'] = replaced['carrier'];
    }
  }
  return { changes, previous };
};

/** One row per field a save changed, newest save first, so the last hand on a value is the top row. */
export const editLogRows = (
  events: FulfillmentEvent[],
  lines: Array<Pick<FulfillmentOrderLine, 'lineNo' | 'sku'>>,
): EditLogRow[] => {
  const skuByLine = new Map(lines.map((line) => [line.lineNo, line.sku]));
  return (
    events
      .filter((event) => EDIT_LOG_OPERATIONS.has(event.operation))
      // An unreadable time sorts last rather than leaving the order to whatever the server sent.
      .sort((a, b) => (instantMs(b.occurredAt) ?? 0) - (instantMs(a.occurredAt) ?? 0))
      .flatMap((event) => {
        const payload = payloadOf(event);
        const { changes, previous } = valuesOf(event.operation, payload);
        // Only the fields the editors save; anything else in the payload is bookkeeping, not a value someone typed.
        const fields = FIELD_ORDER.filter((field) => field in changes);
        return fields.map((field) => ({
          id: `${event.id}:${field}`,
          at: kstTimestamp(event.occurredAt),
          // A gig worker's write has no actor; its source says who it was (SSD-7779).
          actor: event.actor || (event.source === 'GIG' ? 'Gig worker' : '—'),
          line: lineText(payload['lineNo'], skuByLine),
          field: FIELD_LABELS[field] ?? field,
          value: editValueText(field, changes[field]),
          previous: !(field in previous)
            ? PREVIOUS_NOT_RECORDED
            : previous[field] === null && field !== 'lineDeliveryCompany'
              ? NONE
              : editValueText(field, previous[field]),
        }));
      })
  );
};
